<template>
  <div
    class="bill-notes-chat"
    :class="{
      'bill-notes-chat--open': open,
      'bill-notes-chat--client': role === 'client',
      'bill-notes-chat--vendor': role === 'vendor'
    }"
  >
    <Transition name="bill-notes-chat-fade">
      <div
        v-if="open"
        class="bill-notes-chat__overlay"
        role="presentation"
      >
        <div
          class="bill-notes-chat__backdrop"
          aria-hidden="true"
        />
        <div
          class="bill-notes-chat__panel"
          role="dialog"
          aria-modal="true"
          aria-label="Bill notes"
          @click.stop
        >
          <header class="bill-notes-chat__header">
            <div class="bill-notes-chat__peer">
              <div class="bill-notes-chat__peer-avatar" aria-hidden="true">
                <img
                  v-if="resolvedTheirAvatarUrl"
                  :src="resolvedTheirAvatarUrl"
                  alt=""
                  class="bill-notes-chat__avatar-img"
                >
                <span v-else class="bill-notes-chat__avatar-initials bill-notes-chat__avatar-initials--theirs">
                  {{ theirInitials }}
                </span>
              </div>
              <div class="bill-notes-chat__peer-text">
                <h2 class="bill-notes-chat__title">{{ chatTitle }}</h2>
                <p class="bill-notes-chat__subtitle">{{ chatSubtitle }}</p>
              </div>
            </div>
            <button
              type="button"
              class="bill-notes-chat__close"
              title="Close bill notes"
              aria-label="Close bill notes"
              @click="close"
            >
              <i class="bi bi-x-lg" aria-hidden="true" />
            </button>
          </header>

          <div class="bill-notes-chat__picker">
            <LabelDropdown
              :label="documentLabel"
              select-id="bill-notes-bill"
              v-model="selectedBillId"
              flex
              hide-label
              :aria-label="documentLabel"
              :disabled="loadingBills || !billOptions.length"
              :options="invoiceDropdownOptions"
              menu-class="bill-notes-chat__dropdown-menu"
              :menu-z-index="1300"
              @change="onBillChange"
            />
          </div>

          <div ref="thread" class="bill-notes-chat__thread" role="log" aria-label="Bill notes conversation">
            <p v-if="!selectedBillId" class="bill-notes-chat__hint">
              Select a {{ documentLabelLower }} to leave a note for the {{ counterpartLabel }}.
            </p>
            <p v-else-if="loadingNotes" class="bill-notes-chat__hint">
              Loading notes…
            </p>
            <p v-else-if="!notes.length" class="bill-notes-chat__hint">
              No notes yet. Leave the first note for this {{ documentLabelLower }}.
            </p>
            <div v-else class="bill-notes-chat__messages">
              <div
                v-for="note in notes"
                :key="note.id"
                class="bill-notes-chat__row"
                :class="isMine(note) ? 'bill-notes-chat__row--mine' : 'bill-notes-chat__row--theirs'"
              >
                <div class="bill-notes-chat__bubble-wrap">
                  <div class="bill-notes-chat__bubble">
                    <p class="bill-notes-chat__text">{{ note.body }}</p>
                    <div class="bill-notes-chat__footer">
                      <span class="bill-notes-chat__time">
                        <template v-if="note.pending">Sending…</template>
                        <template v-else>{{ formatWhen(note.createdAt) }}</template>
                      </span>
                      <i
                        v-if="isMine(note) && !note.pending"
                        class="bi bi-check2-all bill-notes-chat__ticks"
                        aria-hidden="true"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <p v-if="sendError" class="bill-notes-chat__error">{{ sendError }}</p>

          <form class="bill-notes-chat__composer" @submit.prevent="send">
            <input
              ref="input"
              v-model="draft"
              type="text"
              class="bill-notes-chat__input"
              placeholder="Type a note…"
              autocomplete="off"
              maxlength="4000"
              :disabled="!selectedBillId || sending"
              aria-label="Bill note"
            >
            <button
              type="submit"
              class="bill-notes-chat__send"
              :disabled="!selectedBillId || !draft.trim() || sending"
              aria-label="Send note"
            >
              <i class="bi bi-send-fill" aria-hidden="true" />
            </button>
          </form>
        </div>
      </div>
    </Transition>

    <button
      v-if="!hideFab"
      type="button"
      class="bill-notes-chat__fab"
      :aria-expanded="open ? 'true' : 'false'"
      :aria-label="fabAriaLabel"
      :title="fabTitle"
      @click="toggle"
    >
      <span class="bill-notes-chat__fab-glow" aria-hidden="true"></span>
      <span class="bill-notes-chat__fab-ring" aria-hidden="true"></span>
      <i
        :class="open ? 'bi bi-x-lg' : (role === 'client' ? 'bi bi-bell-fill' : 'bi bi-chat-left-text-fill')"
        aria-hidden="true"
      />
      <span
        v-if="!open && badgeCount > 0"
        class="bill-notes-chat__fab-badge"
        aria-hidden="true"
      >{{ badgeCount > 99 ? '99+' : badgeCount }}</span>
    </button>
  </div>
</template>

<script>
import ApiService from '@/services/api.service'
import LabelDropdown from '@/modules/consultant/components/LabelDropdown.vue'
import vendorPortal from '@/modules/vendor-portal/vendorPortal.service'
import {
  SUBMITTED_INVOICES_SEEN_EVENT,
  VENDOR_SUBMITTED_INVOICE_EVENT,
  countUnseenSubmittedInvoices,
  markSubmittedInvoicesSeen,
  readSeenSubmittedInvoiceIds,
  rememberPendingSubmittedInvoice
} from '@/modules/vendor/utils/submittedInvoiceSeen'

export default {
  name: 'BillNotesChat',
  components: { LabelDropdown },
  props: {
    /** vendor = vendor portal; client = company AnnaBooks user */
    role: {
      type: String,
      required: true,
      validator: (value) => ['vendor', 'client'].includes(value)
    },
    companyId: {
      type: [Number, String],
      default: null
    },
    /** Optional pre-selected bill id */
    billId: {
      type: [Number, String],
      default: null
    },
    /** Hide floating FAB (still openable via bill-notes-open event). */
    hideFab: {
      type: Boolean,
      default: false
    },
    myAvatarUrl: {
      type: String,
      default: ''
    },
    theirAvatarUrl: {
      type: String,
      default: ''
    },
    myName: {
      type: String,
      default: ''
    },
    theirName: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      open: false,
      loadingBills: false,
      loadingNotes: false,
      sending: false,
      sendError: '',
      billOptions: [],
      selectedBillId: this.billId != null && this.billId !== '' ? String(this.billId) : '',
      notes: [],
      draft: '',
      sessionName: '',
      sessionCompanyName: '',
      localMyAvatarUrl: '',
      localTheirAvatarUrl: '',
      selectedVendorName: '',
      unreadCount: 0,
      submittedInvoiceCount: 0,
      submittedPollTimer: null,
      seenNoteIds: {},
      lastNotifiedBillId: '',
      /** Bill currently open in Vendor Portal Hub (or similar) outside this panel. */
      externalViewingBillId: ''
    }
  },
  computed: {
    isVendor() {
      return this.role === 'vendor'
    },
    badgeCount() {
      return Number(this.unreadCount || 0) + Number(this.submittedInvoiceCount || 0)
    },
    fabTitle() {
      if (this.open) return 'Close'
      if (this.submittedInvoiceCount > 0 && !this.isVendor) {
        return `${this.submittedInvoiceCount} submitted invoice${this.submittedInvoiceCount === 1 ? '' : 's'} to verify`
      }
      return 'Notes'
    },
    fabAriaLabel() {
      if (this.open) return 'Close bill notes'
      const parts = []
      if (this.submittedInvoiceCount > 0 && !this.isVendor) {
        parts.push(`${this.submittedInvoiceCount} submitted invoice${this.submittedInvoiceCount === 1 ? '' : 's'}`)
      }
      if (this.unreadCount > 0) {
        parts.push(`${this.unreadCount} new note${this.unreadCount === 1 ? '' : 's'}`)
      }
      if (parts.length) return `Alerts: ${parts.join(', ')}`
      return 'Open bill notes'
    },
    isOnSubmittedReviewPage() {
      if (this.isVendor) return false
      const path = String(this.$route?.path || '')
      return path.includes('/vendors/portal-hub')
    },
    counterpartLabel() {
      return this.isVendor ? 'company' : 'vendor'
    },
    /** Client/owner UI uses “bill”; vendor portal may still say “invoice”. */
    documentLabel() {
      return this.isVendor ? 'Invoice' : 'Bill'
    },
    documentLabelLower() {
      return this.documentLabel.toLowerCase()
    },
    documentLabelPlural() {
      return this.isVendor ? 'invoices' : 'bills'
    },
    chatTitle() {
      return this.resolvedTheirName || (this.isVendor ? 'Company' : 'Vendor')
    },
    chatSubtitle() {
      const selected = this.billOptions.find((b) => String(b.id) === String(this.selectedBillId))
      if (selected?.billNo) return selected.billNo
      return `${this.documentLabel} notes`
    },
    resolvedMyName() {
      return this.myName || this.sessionName || 'You'
    },
    resolvedTheirName() {
      if (this.isVendor) {
        return this.theirName || this.sessionCompanyName || 'Company'
      }
      return this.selectedVendorName || this.theirName || 'Vendor'
    },
    resolvedMyAvatarUrl() {
      return this.myAvatarUrl || this.localMyAvatarUrl || ''
    },
    resolvedTheirAvatarUrl() {
      return this.theirAvatarUrl || this.localTheirAvatarUrl || ''
    },
    myInitials() {
      return this.initialsFrom(this.resolvedMyName)
    },
    theirInitials() {
      return this.initialsFrom(this.resolvedTheirName)
    },
    invoiceDropdownOptions() {
      const placeholder = {
        value: '',
        label: this.loadingBills
          ? `Loading ${this.documentLabelPlural}…`
          : (this.billOptions.length ? `Select a ${this.documentLabelLower}` : `No ${this.documentLabelPlural} yet`)
      }
      const options = this.billOptions.map((bill) => ({
        value: String(bill.id),
        label: this.billLabel(bill)
      }))
      if (this.selectedBillId && !options.some((opt) => opt.value === this.selectedBillId)) {
        const known = this.billOptions.find((b) => String(b.id) === this.selectedBillId)
        options.unshift({
          value: this.selectedBillId,
          label: known?.billNo || `Selected ${this.documentLabelLower}`
        })
      }
      return [placeholder, ...options]
    }
  },
  watch: {
    companyId() {
      if (!this.isVendor) this.refreshSubmittedInvoiceCount()
    },
    isOnSubmittedReviewPage: {
      immediate: true,
      handler(onPage) {
        if (onPage && !this.isVendor) {
          this.refreshSubmittedInvoiceCount({ markSeen: true })
        }
      }
    },
    billId(value) {
      const next = value != null && value !== '' ? String(value) : ''
      if (next && next !== this.selectedBillId) {
        this.selectedBillId = next
        if (this.open) {
          this.loadNotes()
          this.refreshSelectedVendorPeer()
        }
      }
    },
    selectedBillId() {
      if (this.open && !this.isVendor) {
        this.refreshSelectedVendorPeer()
      }
    },
    myAvatarUrl(value) {
      if (value) this.revokeLocalAvatar()
    },
    theirAvatarUrl(value) {
      if (value) this.revokeLocalTheirAvatar()
    }
  },
  beforeUnmount() {
    this.revokeLocalAvatar()
    this.revokeLocalTheirAvatar()
    this.stopSubmittedInvoicePolling()
    if (this._onBillNoteNew) {
      window.removeEventListener('bill-note-new', this._onBillNoteNew)
    }
    if (this._onBillNotesRead) {
      window.removeEventListener('bill-notes-read', this._onBillNotesRead)
    }
    if (this._onBillNotesViewing) {
      window.removeEventListener('bill-notes-viewing', this._onBillNotesViewing)
    }
    if (this._onBillNotesOpen) {
      window.removeEventListener('bill-notes-open', this._onBillNotesOpen)
    }
    if (this._onSubmittedInvoicesSeen) {
      window.removeEventListener(SUBMITTED_INVOICES_SEEN_EVENT, this._onSubmittedInvoicesSeen)
    }
    if (this._onVendorSubmittedInvoice) {
      window.removeEventListener(VENDOR_SUBMITTED_INVOICE_EVENT, this._onVendorSubmittedInvoice)
    }
  },
  mounted() {
    this._onBillNoteNew = this.onBillNoteNew.bind(this)
    this._onBillNotesRead = this.onBillNotesRead.bind(this)
    this._onBillNotesViewing = this.onBillNotesViewing.bind(this)
    this._onBillNotesOpen = this.onBillNotesOpen.bind(this)
    this._onSubmittedInvoicesSeen = this.onSubmittedInvoicesSeen.bind(this)
    this._onVendorSubmittedInvoice = this.onVendorSubmittedInvoice.bind(this)
    window.addEventListener('bill-note-new', this._onBillNoteNew)
    window.addEventListener('bill-notes-read', this._onBillNotesRead)
    window.addEventListener('bill-notes-viewing', this._onBillNotesViewing)
    window.addEventListener('bill-notes-open', this._onBillNotesOpen)
    window.addEventListener(SUBMITTED_INVOICES_SEEN_EVENT, this._onSubmittedInvoicesSeen)
    window.addEventListener(VENDOR_SUBMITTED_INVOICE_EVENT, this._onVendorSubmittedInvoice)
    this.ensureRealtimeSocket()
    if (!this.isVendor) {
      this.refreshSubmittedInvoiceCount()
      this.startSubmittedInvoicePolling()
    }
  },
  methods: {
    isVendorPortalAwaiting(record) {
      if (!record || String(record.type || '').toLowerCase() !== 'bill') return false
      if (String(record.status || '').trim().toLowerCase() !== 'draft') return false
      return Boolean(
        record.isVendorSubmitted === true
        || record.isVendorPortalDraft
      )
    },
    /** Company notes picker: Open + vendor-submitted Draft only (hide Cancelled / other drafts). */
    isClientNotesBillVisible(bill) {
      const status = String(bill?.status || '').trim().toLowerCase()
      if (!status || status === 'cancelled' || status === 'canceled') return false
      if (status === 'open') return true
      if (status === 'draft') {
        return Boolean(bill.isVendorSubmitted || bill.isVendorPortalDraft)
      }
      return false
    },
    onSubmittedInvoicesSeen(event) {
      const detail = event?.detail || {}
      if (detail.companyId != null && this.companyId != null
        && String(detail.companyId) !== String(this.companyId)) {
        return
      }
      this.submittedInvoiceCount = 0
      this.refreshSubmittedInvoiceCount()
    },
    async onVendorSubmittedInvoice(event) {
      if (this.isVendor) return
      const detail = event?.detail || {}
      if (!this.sameCompany(detail)) return
      const billId = detail.billId != null ? String(detail.billId) : ''
      const prev = Number(this.submittedInvoiceCount || 0)
      const seen = readSeenSubmittedInvoiceIds(this.companyId)
      if (billId && !seen.has(billId)) {
        rememberPendingSubmittedInvoice(this.companyId, billId)
        this.submittedInvoiceCount = prev + 1
        this.playNoteSound()
      }
      await this.refreshSubmittedInvoiceCount()
    },
    startSubmittedInvoicePolling() {
      this.stopSubmittedInvoicePolling()
      this.submittedPollTimer = setInterval(() => {
        this.refreshSubmittedInvoiceCount()
      }, 60000)
    },
    stopSubmittedInvoicePolling() {
      if (this.submittedPollTimer) {
        clearInterval(this.submittedPollTimer)
        this.submittedPollTimer = null
      }
    },
    async refreshSubmittedInvoiceCount({ markSeen = false } = {}) {
      if (this.isVendor || !this.companyId) {
        this.submittedInvoiceCount = 0
        return
      }
      try {
        const data = await ApiService.getBillsAndExpenses(this.companyId, {
          view: 'Bills',
          dateFilter: 'All'
        })
        const list = Array.isArray(data) ? data : []
        const awaiting = list.filter((row) => this.isVendorPortalAwaiting(row))
        const shouldMarkSeen = markSeen || this.isOnSubmittedReviewPage
        if (shouldMarkSeen) {
          markSubmittedInvoicesSeen(
            this.companyId,
            awaiting.map((row) => row.id)
          )
          this.submittedInvoiceCount = 0
          return
        }
        this.submittedInvoiceCount = countUnseenSubmittedInvoices(this.companyId, awaiting)
      } catch (_) {
        // Keep last known count on transient errors
      }
    },
    goToSubmittedInvoices() {
      const companyId = this.companyId || this.$route?.params?.id
      if (!companyId || !this.$router) return
      this.open = false
      this.$router.push({
        name: 'VendorPortalHub',
        params: { id: String(companyId) },
      }).catch(() => {})
    },
    ensureRealtimeSocket() {
      if (this.isVendor) {
        import('@/services/vendorPortalSocket')
          .then((m) => m.connectVendorPortalSocket())
          .catch(() => {})
      } else {
        import('@/services/companyPresenceClient')
          .then((m) => m.connectCompanyPresence())
          .catch(() => {})
      }
    },
    playNoteSound() {
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext
        if (!Ctx) return
        const ctx = new Ctx()
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.type = 'sine'
        osc.frequency.value = 880
        gain.gain.value = 0.0001
        osc.connect(gain)
        gain.connect(ctx.destination)
        const now = ctx.currentTime
        gain.gain.exponentialRampToValueAtTime(0.08, now + 0.02)
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.28)
        osc.start(now)
        osc.stop(now + 0.3)
        osc.onended = () => {
          try { ctx.close() } catch (_) { /* ignore */ }
        }
      } catch (_) {
        /* ignore sound errors */
      }
    },
    sameCompany(detail = {}) {
      if (detail.companyId == null || this.companyId == null || this.companyId === '') return true
      return String(detail.companyId) === String(this.companyId)
    },
    onBillNotesViewing(event) {
      const detail = event?.detail || {}
      if (!this.sameCompany(detail)) return
      if (detail.role && String(detail.role) !== this.role) return
      const billId = detail.billId
      this.externalViewingBillId =
        billId != null && billId !== '' ? String(billId) : ''
    },
    async onBillNotesOpen(event) {
      if (this.isVendor) return
      const detail = event?.detail || {}
      if (!this.sameCompany(detail)) return
      const billId = detail.billId != null && detail.billId !== ''
        ? String(detail.billId)
        : ''
      if (billId) {
        this.selectedBillId = billId
        this.lastNotifiedBillId = billId
      }
      this.open = true
      this.unreadCount = 0
      this.ensureRealtimeSocket()
      this.hydrateSessionAvatars()
      await this.bootstrap()
    },
    onBillNotesRead(event) {
      const detail = event?.detail || {}
      if (!this.sameCompany(detail)) return
      if (detail.role && String(detail.role) !== this.role) return
      // Thread was read in Vendor Portal Hub (or another surface) — clear FAB badge
      // the same way opening this panel does.
      this.unreadCount = 0
    },
    onBillNoteNew(event) {
      const detail = event?.detail || {}
      const note = detail.note
      if (!note?.id) return
      if (String(note.authorRole || '') === this.role) return

      const noteKey = String(note.id)
      if (this.seenNoteIds[noteKey]) return
      this.seenNoteIds = { ...this.seenNoteIds, [noteKey]: true }

      const billId = String(detail.billId || note.billId || '')
      if (billId) this.lastNotifiedBillId = billId

      const viewingThisBill = this.open
        && this.selectedBillId
        && String(this.selectedBillId) === billId
      const viewingExternally = !!billId
        && this.externalViewingBillId
        && this.externalViewingBillId === billId

      if (viewingThisBill || viewingExternally) {
        if (viewingThisBill && !this.notes.some((n) => String(n.id) === noteKey)) {
          this.notes.push(note)
          this.$nextTick(() => this.scrollToEnd())
        }
        this.playNoteSound()
        return
      }

      this.unreadCount += 1
      this.playNoteSound()
    },
    initialsFrom(name) {
      const parts = String(name || '')
        .trim()
        .split(/\s+/)
        .filter(Boolean)
      if (!parts.length) return '?'
      if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
      return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase()
    },
    displayAuthorName(note) {
      if (this.isMine(note)) {
        return note.authorName || this.resolvedMyName || 'You'
      }
      return note.authorName || this.resolvedTheirName
    },
    billLabel(bill) {
      const no = String(bill.billNo || '').trim()
      const statusLabel = this.vendorFacingStatusLabel(bill?.status)
      const status = statusLabel ? ` · ${statusLabel}` : ''
      return `${no || this.documentLabel}${status}`
    },
    /** Vendor portal: Draft bills are awaiting company review. */
    vendorFacingStatusLabel(status) {
      const raw = String(status || '').trim()
      if (!raw) return ''
      if (this.isVendor && raw.toLowerCase() === 'draft') return 'In review'
      return raw
    },
    isMine(note) {
      return String(note.authorRole || '') === this.role
    },
    formatWhen(value) {
      if (!value) return ''
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return ''
      return date.toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      })
    },
    revokeLocalAvatar() {
      if (this.localMyAvatarUrl && this.localMyAvatarUrl.startsWith('blob:')) {
        URL.revokeObjectURL(this.localMyAvatarUrl)
      }
      this.localMyAvatarUrl = ''
    },
    revokeLocalTheirAvatar() {
      if (this.localTheirAvatarUrl && this.localTheirAvatarUrl.startsWith('blob:')) {
        URL.revokeObjectURL(this.localTheirAvatarUrl)
      }
      this.localTheirAvatarUrl = ''
    },
    hydrateSessionAvatars() {
      if (!this.isVendor) return
      const session = vendorPortal.getSession() || {}
      this.sessionName = session.vendorName || ''
      this.sessionCompanyName = session.companyName || ''
      if (!this.myAvatarUrl && session.hasLogo) {
        vendorPortal.getLogo()
          .then((response) => {
            this.revokeLocalAvatar()
            this.localMyAvatarUrl = URL.createObjectURL(response.data)
          })
          .catch(() => {
            this.revokeLocalAvatar()
          })
      }
      // Always ensure company logo is available for header (same as company-side vendor logo).
      if (!this.theirAvatarUrl && !this.localTheirAvatarUrl) {
        vendorPortal.getCompanyLogo()
          .then((response) => {
            const blob = response.data
            if (!(blob instanceof Blob) || !String(blob.type || '').startsWith('image/')) {
              this.revokeLocalTheirAvatar()
              return
            }
            this.revokeLocalTheirAvatar()
            this.localTheirAvatarUrl = URL.createObjectURL(blob)
          })
          .catch(() => {
            this.revokeLocalTheirAvatar()
          })
      }
    },
    toggle() {
      // Company alert: pending vendor-portal submissions open Draft Bills → Submitted
      if (
        !this.isVendor
        && !this.open
        && this.submittedInvoiceCount > 0
        && !this.isOnSubmittedReviewPage
      ) {
        this.goToSubmittedInvoices()
        return
      }
      this.open = !this.open
      if (this.open) {
        this.unreadCount = 0
        this.ensureRealtimeSocket()
        this.hydrateSessionAvatars()
        // Prefer route bill, else last notified bill (same UX as company side).
        const preferred = (this.billId != null && this.billId !== '')
          ? String(this.billId)
          : (this.lastNotifiedBillId || this.selectedBillId || '')
        if (preferred) {
          this.selectedBillId = preferred
        }
        this.bootstrap()
      }
    },
    close() {
      this.open = false
      this.unreadCount = 0
    },
    async bootstrap() {
      await this.loadBills()
      if (this.selectedBillId) {
        await Promise.all([this.loadNotes(), this.refreshSelectedVendorPeer()])
      }
      this.$nextTick(() => this.$refs.input?.focus())
    },
    async loadBills() {
      this.loadingBills = true
      try {
        if (this.isVendor) {
          const response = await vendorPortal.listBills()
          this.billOptions = response.data?.bills || []
        } else {
          const companyId = this.companyId
          if (!companyId) {
            this.billOptions = []
            return
          }
          const data = await ApiService.getBillsAndExpenses(companyId, { view: 'All' })
          const rows = Array.isArray(data) ? data : (data?.bills || data?.data || [])
          this.billOptions = rows
            .map((row) => ({
              id: row.id || row.billId,
              billNo: row.billNo || row.no || row.number || row.refNo || '',
              status: row.status,
              vendorId: row.vendorId || null,
              vendorName: row.vendorName || '',
              recordType: String(row.recordType || row.type || 'bill').toLowerCase(),
              isVendorSubmitted: Boolean(row.isVendorSubmitted),
              isVendorPortalDraft: Boolean(row.isVendorPortalDraft)
                || /Submitted from vendor portal/i.test(String(row.comments || ''))
            }))
            .filter((row) => {
              if (!row.id) return false
              const type = row.recordType
              return type === 'bill' || type.includes('bill')
            })
            .filter((row) => String(row.billNo || '').trim())
            .filter((row) => this.isClientNotesBillVisible(row))
          const seen = new Set()
          this.billOptions = this.billOptions.filter((b) => {
            const key = String(b.id)
            if (seen.has(key)) return false
            seen.add(key)
            return true
          })
        }
        if (this.selectedBillId && !this.billOptions.some((b) => String(b.id) === this.selectedBillId)) {
          // keep selection even if not in list (detail deep-link)
        } else if (!this.selectedBillId && this.billOptions.length === 1) {
          this.selectedBillId = String(this.billOptions[0].id)
        }
      } catch {
        this.billOptions = []
      } finally {
        this.loadingBills = false
      }
    },
    onBillChange() {
      this.notes = []
      this.sendError = ''
      this.loadNotes()
      this.refreshSelectedVendorPeer()
    },
    async refreshSelectedVendorPeer() {
      if (this.isVendor) return
      this.revokeLocalTheirAvatar()
      this.selectedVendorName = ''
      if (!this.selectedBillId || !this.companyId) return

      let bill = this.billOptions.find((b) => String(b.id) === String(this.selectedBillId))
      let vendorId = bill?.vendorId || null
      let vendorName = bill?.vendorName || ''

      if (!vendorId) {
        try {
          const detail = await ApiService.getEnterBillById(this.selectedBillId)
          vendorId = detail?.vendorId || detail?.bill?.vendorId || null
          vendorName = detail?.vendorName
            || detail?.vendor?.vendorName
            || detail?.bill?.vendorName
            || ''
        } catch (_) {
          return
        }
      }

      if (!vendorId) return
      this.selectedVendorName = vendorName || 'Vendor'

      try {
        const response = await ApiService.get(
          `/vendor/vendors/${vendorId}/portal-logo?companyId=${encodeURIComponent(this.companyId)}`,
          { responseType: 'blob' }
        )
        const blob = response.data
        if (!(blob instanceof Blob) || !String(blob.type || '').startsWith('image/')) {
          this.revokeLocalTheirAvatar()
          return
        }
        this.revokeLocalTheirAvatar()
        this.localTheirAvatarUrl = URL.createObjectURL(blob)
      } catch (_) {
        this.revokeLocalTheirAvatar()
      }
    },
    async loadNotes() {
      if (!this.selectedBillId) {
        this.notes = []
        return
      }
      this.loadingNotes = true
      this.sendError = ''
      try {
        if (this.isVendor) {
          const response = await vendorPortal.listBillNotes(this.selectedBillId)
          this.notes = response.data?.notes || []
        } else {
          const response = await ApiService.get(`/vendor/enter-bill/${this.selectedBillId}/notes?companyId=${encodeURIComponent(this.companyId)}`)
          this.notes = response.data?.notes || []
        }
        const seen = { ...this.seenNoteIds }
        this.notes.forEach((n) => {
          if (n?.id != null) seen[String(n.id)] = true
        })
        this.seenNoteIds = seen
      } catch {
        this.notes = []
      } finally {
        this.loadingNotes = false
        // Scroll after loading flag clears so message nodes are actually in the DOM.
        this.$nextTick(() => this.scrollToEnd())
      }
    },
    async send() {
      const body = this.draft.trim()
      if (!body || !this.selectedBillId || this.sending) return
      this.sending = true
      this.sendError = ''
      const tempId = `temp-${Date.now()}`
      const optimistic = {
        id: tempId,
        billId: Number(this.selectedBillId),
        authorRole: this.role,
        authorName: this.resolvedMyName || 'You',
        body,
        createdAt: new Date().toISOString(),
        pending: true
      }
      this.notes.push(optimistic)
      this.draft = ''
      this.$nextTick(() => this.scrollToEnd())
      try {
        let note = null
        if (this.isVendor) {
          const response = await vendorPortal.createBillNote(this.selectedBillId, body)
          note = response.data?.note
        } else {
          const response = await ApiService.post(`/vendor/enter-bill/${this.selectedBillId}/notes`, {
            companyId: this.companyId,
            body
          })
          note = response.data?.note
        }
        const idx = this.notes.findIndex((n) => n.id === tempId)
        if (note) {
          if (idx >= 0) this.notes.splice(idx, 1, note)
          else this.notes.push(note)
        } else if (idx >= 0) {
          this.notes.splice(idx, 1)
          this.sendError = 'Note was not saved. Try again.'
          this.draft = body
        }
        this.$nextTick(() => {
          this.scrollToEnd()
          this.$refs.input?.focus()
        })
      } catch (error) {
        this.notes = this.notes.filter((n) => n.id !== tempId)
        this.draft = body
        this.sendError = error.response?.data?.message
          || error.response?.data?.error
          || 'Unable to send the note. Try again.'
        this.$nextTick(() => this.$refs.input?.focus())
      } finally {
        this.sending = false
      }
    },
    scrollToEnd(attempt = 0) {
      const el = this.$refs.thread
      if (!el) return
      el.scrollTop = el.scrollHeight
      const last = el.querySelector('.bill-notes-chat__row:last-child')
      if (last) {
        last.scrollIntoView({ block: 'end', behavior: 'auto' })
      }
      // Retry briefly — panel transition / layout can leave scrollHeight short on first paint.
      if (attempt < 4) {
        requestAnimationFrame(() => this.scrollToEnd(attempt + 1))
      }
    }
  }
}
</script>

<style src="./BillNotesChat.css"></style>
